import React, { useState, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { Alert, Button } from "@mui/material";
import { Stack } from "../../layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import AddNewUserDrawer from "../users/AddNewUserDrawer";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import EmployeeSearchAutoComplete, {
  employeeFromUserNode,
  type Employee,
} from "../../common/EmployeeSearchAutoComplete";
import { GET_USERS } from "../../../graphql/user";
import UserStatus from "enums/UserStatus";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useDrawerRecord } from "hooks/useDrawerRecord";
export type { Employee } from "../../common/EmployeeSearchAutoComplete";

interface EmployeeSearchProps {
  employee: Employee | null;
  onSelectEmployee: (employee: Employee | null) => void;
  onHasAddressChange?: (hasAddress: boolean) => void;
}

const EmployeeSearch: React.FC<EmployeeSearchProps> = ({
  employee,
  onSelectEmployee,
  onHasAddressChange,
}) => {
  const { t } = useTranslation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  const [isShowAddNewUserDrawer, setIsShowAddNewUserDrawer] =
    useState<boolean>(false);

  const profileDrawer = useDrawerRecord<string>();
  const [fetchUsers] = useLazyQuery(GET_USERS, {
    fetchPolicy: "network-only",
  });

  const handleUpdatedUser = useCallback(async () => {
    if (!employee?.id) return;
    const { data } = await fetchUsers({
      variables: {
        searchTerm: employee.email,
        status: [UserStatus.ACTIVE, UserStatus.PENDING],
        first: 10,
      },
    });
    const node = (data?.users?.edges ?? [])
      .map((edge: { node: Employee }) => edge.node)
      // Search by email can return more than one row, so match the id.
      .find((candidate: Employee) => candidate.id === employee.id);
    if (node) onSelectEmployee(employeeFromUserNode(node));
  }, [employee?.id, employee?.email, fetchUsers, onSelectEmployee]);

  const handleAddAddressClick = () => {
    if (employee && employee.id) {
      profileDrawer.show(employee.id);
    }
  };

  useEffect(() => {
    if (employee && onHasAddressChange) {
      onHasAddressChange(!!employee.address);
    }
  }, [employee, onHasAddressChange]);

  return (
    <Stack gap="var(--space-4)">
      <EmployeeSearchAutoComplete
        title={t("Search user")}
        handleSelectEmployee={onSelectEmployee}
        employee={employee}
        onAddUser={
          isAdminOrHigher ? () => setIsShowAddNewUserDrawer(true) : undefined
        }
      />

      {employee &&
        (!employee.address || !employee.phone || !employee.birthdate) && (
          <Alert
            severity="warning"
            role="alert"
            action={
              <Button
                variant="primary-outlined"
                onClick={handleAddAddressClick}
              >
                {t("Add Information")}
              </Button>
            }
          >
            {t("UserRequiredInfo")}
          </Alert>
        )}

      <AddNewUserDrawer
        open={isShowAddNewUserDrawer}
        handleCloseDrawer={() => {
          setIsShowAddNewUserDrawer(false);
        }}
        handleCreatedUser={(error?: string | null, user?: Employee) => {
          if (user) {
            onSelectEmployee(user);
          }
        }}
      />

      {profileDrawer.record && (
        <UserProfileDrawer
          selectedUserId={profileDrawer.record}
          open={profileDrawer.open}
          handleCloseDrawer={profileDrawer.close}
          onAfterClose={profileDrawer.afterClose}
          handleUpdateUser={handleUpdatedUser}
        />
      )}
    </Stack>
  );
};

export default EmployeeSearch;
